import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  ButtonGroup,
  Divider
} from '@mui/material';
import { 
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon 
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { pl } from 'date-fns/locale';
import { format } from 'date-fns';

const locationNames = {
  PAN: 'Panorama M&M GOLD',
  VVS: 'Panorama VVS',
  MODO: 'Modo M&M GOLD',
  KLIF: 'Klif M&M GOLD',
  GP: 'Galeria Północna M&M GOLD',
  SB: 'Stary Browar M&M GOLD',
  MB: 'Marbella'
};

const locationHours = {
  PAN: { start: '11:00', end: '20:00' },
  VVS: { start: '11:00', end: '20:00' },
  MODO: { start: '10:00', end: '21:00' },
  KLIF: { start: '09:00', end: '21:00' },
  GP: { start: '10:00', end: '22:00' },
  SB: { start: '09:00', end: '21:00' },
  MB: { start: '10:00', end: '21:00' }
};

const formatUserName = (user) => {
  if (!user) return '';
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  return user.email;
};

const ShiftForm = ({ 
  open, 
  onClose, 
  onSubmit,
  onDelete,
  onApprove,
  onReject,
  selectedDate,
  isAdmin,
  employees = [],
  initialData,
  currentUser,
  defaultLocation
}) => {
  const [formData, setFormData] = useState({
    date: selectedDate,
    locationId: '',
    employeeId: '',
    startTime: '',
    endTime: ''
  });

  useEffect(() => {
    if (initialData) {
      // Znajdź ID lokalizacji na podstawie wartości z backendu
      console.log('ShiftForm - initialData:', initialData);
      console.log('ShiftForm - initialData.location:', initialData.location);
      
      let locationId;
      if (initialData.location.startsWith('Location.')) {
        // Jeśli przychodzi np. "Location.GP", wyciągamy "GP"
        locationId = initialData.location.split('.')[1];
      } else {
        // Szukamy po pełnej nazwie
        locationId = Object.entries(locationNames).find(
          ([_, name]) => name === initialData.location
        )?.[0];
      }

      console.log('ShiftForm - znalezione locationId:', locationId);

      if (!locationId) {
        console.error('ShiftForm - nie znaleziono locationId dla:', initialData.location);
      }

      setFormData({
        date: new Date(initialData.date),
        locationId: locationId || '',
        employeeId: initialData.user.uid,
        startTime: initialData.start_time,
        endTime: initialData.end_time
      });
    } else {
      // Jeśli nie ma initialData, ustaw domyślną lokalizację jeśli jest wybrana
      const newFormData = {
        date: selectedDate,
        locationId: defaultLocation || '',
        employeeId: currentUser.uid,
        startTime: defaultLocation ? locationHours[defaultLocation]?.start || '' : '',
        endTime: defaultLocation ? locationHours[defaultLocation]?.end || '' : ''
      };
      setFormData(newFormData);
    }
  }, [initialData, selectedDate, currentUser, defaultLocation]);

  const handleLocationChange = (event) => {
    const locationId = event.target.value;
    setFormData(prev => ({
      ...prev,
      locationId,
      startTime: locationHours[locationId]?.start || '',
      endTime: locationHours[locationId]?.end || ''
    }));
  };

  const handleDelete = () => {
    if (window.confirm('Czy na pewno chcesz usunąć tę zmianę?')) {
      onDelete(initialData.id);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        fontFamily: 'Cinzel, serif'
      }}>
        {initialData ? 'Edytuj zmianę' : 'Dodaj nową zmianę'}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAdmin && initialData && initialData.status === 'pending' && (
            <ButtonGroup size="small">
              <Button
                startIcon={<CheckIcon />}
                onClick={() => onApprove(initialData.id)}
                color="success"
                variant="contained"
              >
                Zatwierdź
              </Button>
              <Button
                startIcon={<CloseIcon />}
                onClick={() => onReject(initialData.id)}
                color="error"
                variant="outlined"
              >
                Odrzuć
              </Button>
            </ButtonGroup>
          )}
          {isAdmin && initialData && (
            <IconButton 
              onClick={handleDelete}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Data: {format(new Date(formData.date), 'dd.MM.yyyy')}
          </Typography>

          {initialData && (
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 2,
                color: initialData.status === 'approved' ? 'success.main' : 
                       initialData.status === 'rejected' ? 'error.main' : 
                       'warning.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              Status: {
                initialData.status === 'approved' ? (
                  <>
                    <CheckIcon fontSize="small" color="success" />
                    Zatwierdzony
                  </>
                ) : initialData.status === 'rejected' ? (
                  <>
                    <CloseIcon fontSize="small" color="error" />
                    Odrzucony
                  </>
                ) : (
                  <>
                    <Typography component="span" sx={{ color: 'warning.main' }}>⏳</Typography>
                    Oczekujący
                  </>
                )
              }
              {initialData.approved_by && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Zatwierdzone przez: {formatUserName(initialData.approved_by)}
                </Typography>
              )}
            </Typography>
          )}

          {!isAdmin && !initialData && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              ⓘ Twoja zmiana będzie wymagała zatwierdzenia przez administratora
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <FormControl fullWidth margin="normal">
            <InputLabel>Lokalizacja</InputLabel>
            <Select
              value={formData.locationId}
              onChange={handleLocationChange}
              label="Lokalizacja"
              required
            >
              {Object.entries(locationNames).map(([id, name]) => (
                <MenuItem key={id} value={id}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {isAdmin && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Pracownik</InputLabel>
              <Select
                value={formData.employeeId}
                onChange={(e) => setFormData(prev => ({ ...prev, employeeId: e.target.value }))}
                label="Pracownik"
                required
              >
                {employees.map(employee => (
                  <MenuItem key={employee.id} value={employee.id.toString()}>
                    {employee.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TimePicker
                label="Godzina rozpoczęcia"
                value={formData.startTime ? new Date(`2000-01-01T${formData.startTime}`) : null}
                onChange={(newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    startTime: newValue ? format(newValue, 'HH:mm') : ''
                  }));
                }}
                renderInput={(params) => <TextField {...params} fullWidth required />}
              />
              <TimePicker
                label="Godzina zakończenia"
                value={formData.endTime ? new Date(`2000-01-01T${formData.endTime}`) : null}
                onChange={(newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    endTime: newValue ? format(newValue, 'HH:mm') : ''
                  }));
                }}
                renderInput={(params) => <TextField {...params} fullWidth required />}
              />
            </Box>
          </LocalizationProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button onClick={() => onSubmit({
          ...formData,
          created_by_id: currentUser.uid // Dodajemy ID zalogowanego użytkownika
        })} variant="contained" color="primary">
          {initialData ? 'Zapisz zmiany' : 'Dodaj'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShiftForm;
